/**
 * Modal-style form for creating a webhook subscription.
 *
 * Two-step UX:
 *   1. Form: name + target_url + event_kinds + filters + redaction toggles.
 *      Validates client-side (HTTPS, event_kinds non-empty); the server
 *      re-validates via the egress guard.
 *   2. Secret reveal: on POST success the API returns a 32-byte signing
 *      secret ONCE. We surface it in place of the form with a copy button +
 *      explicit "I've stored this safely" confirm. Closing it, whether by
 *      confirm or by the close button, clears the secret from React state
 *      so it can never be re-rendered.
 *
 * In the Connections sheet the form is `bare`: no card of its own and no
 * buttons, because the sheet's footer submits it by `formId`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { WEBHOOK_EVENT_KINDS, type WebhookEventKind } from "@yagni/shared";
import { Button } from "../../ui/button.js";
import { Input } from "../../ui/input.js";
import { Checkbox } from "../../ui/checkbox.js";
import { FormField } from "../../ui/form-field.js";
import { Label } from "../../ui/label.js";
import type {
  AgentOption,
  ChannelOption,
  CreateWebhookPayload,
  CreateWebhookResponse,
  WebhookSubscription,
} from "./types.js";

const EVENT_KIND_LABELS: Record<WebhookEventKind, string> = {
  "channel.message_posted": "Channel message posted",
  "channel.message_mentioned": "Channel message @-mentioned",
  "ticket.created": "Ticket created",
  "ticket.phase_changed": "Ticket phase changed",
  "ticket.commented": "Ticket commented",
  "review.completed": "Code review completed",
};

interface FormState {
  name: string;
  targetUrl: string;
  eventKinds: Set<WebhookEventKind>;
  channelIds: Set<string>;
  agentIds: Set<string>;
  includeMessageBodies: boolean;
  includeAttachments: boolean;
  submitting: boolean;
  error: string | null;
}

const INITIAL: FormState = {
  name: "",
  targetUrl: "",
  eventKinds: new Set<WebhookEventKind>(),
  channelIds: new Set<string>(),
  agentIds: new Set<string>(),
  includeMessageBodies: true,
  includeAttachments: true,
  submitting: false,
  error: null,
};

export interface SubscriptionFormProps {
  channels?: ChannelOption[];
  agents?: AgentOption[];
  onCreated?: (subscription: WebhookSubscription) => void;
  onCancel?: () => void;
  /**
   * Test seam — defaults to the global `fetch`. Tests that don't want to
   * stub `globalThis.fetch` can pass in a fake here. Production callers
   * leave this undefined.
   */
  fetchImpl?: typeof fetch;
  /** The form's id, so a sheet footer outside it can submit it. */
  formId?: string;
  /** In a sheet: no card and no buttons of its own. */
  bare?: boolean;
  /** Told when the once-shown secret appears and when it is gone. */
  onSecretChange?: (shown: boolean) => void;
  /** Told whenever a submit starts or settles. */
  onSubmittingChange?: (submitting: boolean) => void;
}

export function SubscriptionForm({
  channels = [],
  agents = [],
  onCreated,
  onCancel,
  fetchImpl,
  formId,
  bare = false,
  onSecretChange,
  onSubmittingChange,
}: SubscriptionFormProps) {
  const [s, setState] = useState<FormState>(INITIAL);
  const [secret, setSecret] = useState<{
    value: string;
    subscription: WebhookSubscription;
  } | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [copyAck, setCopyAck] = useState<string | null>(null);
  const copyAckTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (copyAckTimer.current !== null) {
      window.clearTimeout(copyAckTimer.current);
    }
  }, []);

  const shown = secret !== null;
  useEffect(() => {
    onSecretChange?.(shown);
  }, [shown, onSecretChange]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  function toggleEventKind(kind: WebhookEventKind) {
    setState((prev) => {
      const next = new Set(prev.eventKinds);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return { ...prev, eventKinds: next };
    });
  }

  function toggleSetEntry(field: "channelIds" | "agentIds", id: string) {
    setState((prev) => {
      const next = new Set(prev[field]);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, [field]: next };
    });
  }

  function dismissSecret() {
    setSecret(null);
    setConfirmed(false);
    setCopyAck(null);
  }

  async function copySecret() {
    if (!secret) return;
    try {
      await navigator.clipboard?.writeText(secret.value);
      setCopyAck("Copied to clipboard");
    } catch {
      setCopyAck("Copy failed. Select the value by hand.");
    }
    if (copyAckTimer.current !== null) {
      window.clearTimeout(copyAckTimer.current);
    }
    copyAckTimer.current = window.setTimeout(() => setCopyAck(null), 4000);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (s.submitting) return;
    update("error", null);

    if (!s.name.trim()) return update("error", "Name is required");
    if (!s.targetUrl.trim().startsWith("https://"))
      return update("error", "Target URL must use https://");
    if (s.eventKinds.size === 0)
      return update("error", "Select at least one event kind");

    update("submitting", true);
    onSubmittingChange?.(true);
    const payload: CreateWebhookPayload = {
      name: s.name.trim(),
      target_url: s.targetUrl.trim(),
      event_kinds: Array.from(s.eventKinds),
      channel_ids: Array.from(s.channelIds),
      agent_ids: Array.from(s.agentIds),
      include_message_bodies: s.includeMessageBodies,
      include_attachments: s.includeAttachments,
    };
    try {
      const fetcher = fetchImpl ?? fetch;
      const res = await fetcher("/api/webhook-subscriptions", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Create failed (${res.status})`);
      }
      const body = (await res.json()) as CreateWebhookResponse;
      setState(INITIAL);
      setSecret({
        value: body.signing_secret,
        subscription: body.subscription,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        submitting: false,
        error: err instanceof Error ? err.message : "Unknown error",
      }));
      onSubmittingChange?.(false);
      return;
    }
    setState((prev) => ({ ...prev, submitting: false }));
    onSubmittingChange?.(false);
  }

  const eventKindList = useMemo(
    () =>
      WEBHOOK_EVENT_KINDS.map((kind) => ({
        kind,
        label: EVENT_KIND_LABELS[kind],
      })),
    [],
  );

  function handleConfirm() {
    if (!secret) return;
    const sub = secret.subscription;
    setConfirmed(true);
    dismissSecret();
    onCreated?.(sub);
  }

  return (
    <>
      {!(bare && secret) && (
      <form
        id={formId}
        onSubmit={submit}
        className={
          bare
            ? "flex flex-col gap-md"
            : "flex flex-col gap-md rounded-[var(--radius-card)] border border-[var(--print-border)] bg-[var(--print-bg)] shadow-[var(--shadow-print)] p-md"
        }
        data-testid="webhook-subscription-form"
        aria-label="Create webhook subscription"
      >
        <FormField label="Name" htmlFor="webhook-name">
          <Input
            id="webhook-name"
            type="text"
            value={s.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Forward to Slack mirror"
            maxLength={80}
            required
          />
        </FormField>

        <FormField
          label="Target URL"
          htmlFor="webhook-target-url"
          hint="HTTPS only. We sign every request with HMAC-SHA256."
        >
          <Input
            id="webhook-target-url"
            type="url"
            value={s.targetUrl}
            onChange={(e) => update("targetUrl", e.target.value)}
            placeholder="https://hooks.example.com/yagni"
            required
          />
        </FormField>

        <fieldset
          className="flex flex-col gap-xs rounded-md border border-[var(--border)] p-sm"
          aria-label="Event kinds"
        >
          <legend className="text-sm font-medium px-xs">Event kinds</legend>
          {eventKindList.map(({ kind, label }) => {
            const id = `event-kind-${kind}`;
            return (
              <div key={kind} className="flex items-center gap-sm text-sm">
                <Checkbox
                  id={id}
                  checked={s.eventKinds.has(kind)}
                  onCheckedChange={() => toggleEventKind(kind)}
                  data-testid={id}
                />
                <Label htmlFor={id} className="text-sm font-normal">
                  <span className="font-mono text-xs">{kind}</span>
                  <span className="text-[var(--text-secondary)]"> · {label}</span>
                </Label>
              </div>
            );
          })}
        </fieldset>

        {channels.length > 0 && (
          <fieldset
            className="flex flex-col gap-xs rounded-md border border-[var(--border)] p-sm"
            aria-label="Channel filter"
          >
            <legend className="text-sm font-medium px-xs">
              Channel filter (leave empty for all channels)
            </legend>
            <div className="flex flex-wrap gap-sm">
              {channels.map((channel) => {
                const id = `webhook-channel-${channel.id}`;
                return (
                  <div key={channel.id} className="flex items-center gap-xs text-sm">
                    <Checkbox
                      id={id}
                      checked={s.channelIds.has(channel.id)}
                      onCheckedChange={() => toggleSetEntry("channelIds", channel.id)}
                    />
                    <Label htmlFor={id} className="text-sm font-normal">
                      {channel.name}
                    </Label>
                  </div>
                );
              })}
            </div>
          </fieldset>
        )}

        {agents.length > 0 && (
          <fieldset
            className="flex flex-col gap-xs rounded-md border border-[var(--border)] p-sm"
            aria-label="Agent filter"
          >
            <legend className="text-sm font-medium px-xs">
              Agent filter (leave empty for all agents)
            </legend>
            <div className="flex flex-wrap gap-sm">
              {agents.map((agent) => {
                const id = `webhook-agent-${agent.id}`;
                return (
                  <div key={agent.id} className="flex items-center gap-xs text-sm">
                    <Checkbox
                      id={id}
                      checked={s.agentIds.has(agent.id)}
                      onCheckedChange={() => toggleSetEntry("agentIds", agent.id)}
                    />
                    <Label htmlFor={id} className="text-sm font-normal">
                      {agent.name}
                    </Label>
                  </div>
                );
              })}
            </div>
          </fieldset>
        )}

        <fieldset
          className="flex flex-col gap-xs rounded-md border border-[var(--border)] p-sm"
          aria-label="Payload redaction"
        >
          <legend className="text-sm font-medium px-xs">Payload contents</legend>
          <div className="flex items-center gap-sm text-sm">
            <Checkbox
              id="webhook-include-bodies"
              checked={s.includeMessageBodies}
              onCheckedChange={(c) => update("includeMessageBodies", Boolean(c))}
            />
            <Label htmlFor="webhook-include-bodies" className="text-sm font-normal">
              Include message bodies in payloads.
            </Label>
          </div>
          <div className="flex items-center gap-sm text-sm">
            <Checkbox
              id="webhook-include-attachments"
              checked={s.includeAttachments}
              onCheckedChange={(c) => update("includeAttachments", Boolean(c))}
            />
            <Label htmlFor="webhook-include-attachments" className="text-sm font-normal">
              Include attachment metadata in payloads.
            </Label>
          </div>
        </fieldset>

        {s.error && (
          <div role="alert" className="text-sm text-[var(--error)]">
            {s.error}
          </div>
        )}

        {!bare && (
        <div className="flex gap-sm justify-end">
          {onCancel && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="text-xs"
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            size="sm"
            className="text-xs"
            loading={s.submitting}
          >
            Create webhook
          </Button>
        </div>
        )}
      </form>
      )}

      {secret && (
        <div
          role="region"
          aria-labelledby="webhook-secret-title"
          data-testid="webhook-secret-modal"
          className="stopbox"
        >
          <div>
            <header className="flex items-start justify-between gap-md mb-sm">
              <h2 id="webhook-secret-title" className="text-lg font-semibold">
                Save this signing secret. You will not see it again.
              </h2>
              <button
                type="button"
                onClick={dismissSecret}
                aria-label="Close"
                className="text-sm text-[var(--text-secondary)]"
              >
                ×
              </button>
            </header>
            <p className="text-sm text-[var(--text-secondary)] mb-sm">
              Use this secret to verify the HMAC-SHA256 signature on the{" "}
              <code>X-YAGNI-Signature</code> header for every webhook delivery.
              We cannot show this value again; regenerating it replaces it
              immediately.
            </p>
            <div className="flex items-center gap-xs mb-sm">
              <Input
                type="text"
                readOnly
                value={secret.value}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 font-mono text-sm"
                data-testid="webhook-secret-value"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="text-xs"
                onClick={() => void copySecret()}
                data-testid="webhook-secret-copy"
              >
                Copy
              </Button>
            </div>
            {copyAck && (
              <div className="text-xs text-[var(--text-secondary)] mb-sm" role="status">
                {copyAck}
              </div>
            )}
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                className="text-xs"
                onClick={handleConfirm}
                data-testid="webhook-secret-confirm"
              >
                I've stored this safely
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmed flag is referenced only to satisfy the linter — the
          live state lives via dismissSecret/handleConfirm. */}
      {confirmed && null}
    </>
  );
}
